import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTask } from '../contexts/TaskContext';
import { PRIORITIES, extractPlainText } from '../lib/constants';
import { getDefaultFields } from '../lib/viewFields';
import { CheckCircle2, Circle, Clock, Calendar, CalendarClock, User, Tag, Paperclip, ArrowUp } from 'lucide-react';
import ProjectChip from './ProjectChip';
import { statusValueName } from '../lib/statusName';
const GV_STATUS_COLORS = {
  todo: 'bg-gray-500/15 text-muted-foreground',
  in_progress: 'bg-blue-500/15 text-blue-400',
  completed: 'bg-green-500/15 text-green-500',
  archived: 'bg-zinc-500/15 text-zinc-400',
};

function gvRenderCustom(def, raw, language) {
  if (raw == null || raw === '') return null;
  if (def.field_type === 'checkbox') return raw ? '✓' : '-';
  if (def.field_type === 'multi_select' && Array.isArray(raw)) {
    return raw.map(id => def.options?.find(o => o.id === id)?.label).filter(Boolean).join(', ') || null;
  }
  if (def.field_type === 'select') return def.options?.find(o => o.id === raw)?.label || null;
  if (def.field_type === 'date') { try { return new Date(raw).toLocaleDateString(language); } catch { return String(raw); } }
  return String(raw);
}

/**
 * GalleryView - Pinterest-style card grid for visual task overview.
 *
 * Two extra modes for the public team page:
 *   • `readOnly`     - disables click handlers and the toggle button.
 *   • `showUpvotes`  - renders an ↑/count chip on each card and re-sorts cards
 *                      by `upvote_count` desc so the most-supported ideas float
 *                      to the top. Use it for `public_visibility=contribute`
 *                      projects (the "Ideas" portal scenario).
 */
const GalleryView = ({
  tasks = [],
  projects = [],
  onTaskClick,
  visibleFields = null,
  customFieldDefs = [],
  readOnly = false,
  showUpvotes = false,
  onUpvote,
  onSignInRequired,
  isAuthenticated = true,
}) => {
  const { t } = useTranslation();
  const { formatDate, language } = useDateTime();
  const { openTaskDetail } = useTask();
  const fields = useMemo(() => {
    if (Array.isArray(visibleFields)) return new Set(visibleFields);
    return new Set([...getDefaultFields('gallery'), ...customFieldDefs.map(d => `cf_${d.id}`)]);
  }, [visibleFields, customFieldDefs]);
  const show = (key) => fields.has(key);

  // When upvotes drive the sort (ideas portal), surface the most-voted cards
  // first; ties break by most recent so brand-new ideas don't get buried.
  const sortedTasks = useMemo(() => {
    if (!showUpvotes) return tasks;
    return tasks.slice().sort((a, b) => {
      const dv = (b.upvote_count || 0) - (a.upvote_count || 0);
      if (dv !== 0) return dv;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [tasks, showUpvotes]);

  const handleTaskClick = (task) => {
    if (readOnly) return;
    if (onTaskClick) {
      onTaskClick(task);
    } else {
      openTaskDetail(task.id);
    }
  };

  const handleUpvoteClick = (e, task) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      onSignInRequired?.();
      return;
    }
    onUpvote?.(task.id);
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-1">
      {sortedTasks.map(task => {
        const priority = PRIORITIES[task.priority] || PRIORITIES.p4;
        const project = getProjectById(task.project_id);
        const isCompleted = task.status === 'completed';
        const description = extractPlainText(task.description);
        const hasAttachments = task.attachments?.length > 0;
        const coverImage = hasAttachments
          ? task.attachments.find(a => a.content_type?.startsWith('image/'))
          : null;

        return (
          <div
            key={task.id}
            onClick={() => handleTaskClick(task)}
            className={`relative bg-popover border border-border rounded-xl overflow-hidden hover:border-border hover:shadow-lg transition-all group ${readOnly ? '' : 'cursor-pointer'} ${
              isCompleted ? 'opacity-60' : ''
            }`}
          >
            {/* Upvote chip (ideas portal) - anchored top-left over the card */}
            {showUpvotes && (
              <button
                type="button"
                onClick={(e) => handleUpvoteClick(e, task)}
                aria-label={t('publicTeam.ideas.upvote', { defaultValue: 'Upvote' })}
                className={`absolute top-2 left-2 z-10 flex flex-col items-center justify-center w-10 h-12 rounded-md border text-[11px] font-semibold transition-colors ${
                  task.has_upvoted
                    ? 'bg-amber-500/15 border-amber-500/60 text-amber-700 dark:text-amber-400'
                    : 'bg-card/80 border-border text-foreground/80 hover:border-input hover:text-foreground'
                }`}
              >
                <ArrowUp className="w-3.5 h-3.5" />
                <span>{task.upvote_count || 0}</span>
              </button>
            )}

            {/* Cover image (if attachment exists) */}
            {coverImage && (
              <div className="h-32 bg-muted overflow-hidden">
                <img
                  src={coverImage.url}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
            )}

            {/* Card body */}
            <div className={`p-3 space-y-2 ${showUpvotes ? 'pl-14' : ''}`}>
              {/* Priority badge + project + status */}
              {(show('project') || show('priority') || show('status')) && (
                <div className="flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {show('project') && (
                      <span className="text-xs text-muted-foreground">
                        {project
                          ? <ProjectChip project={project} />
                          : <span className="text-muted-foreground/50">-</span>}
                      </span>
                    )}
                    {show('status') && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${GV_STATUS_COLORS[task.status] || GV_STATUS_COLORS.todo}`}>
                        {!task.status || GV_STATUS_COLORS[task.status] ? statusValueName(task.status || 'todo', t) : task.status}
                      </span>
                    )}
                  </div>
                  {show('priority') && (
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded shrink-0 ${priority.bgLight} ${priority.textColor}`}>
                      {priority.shortLabel}
                    </span>
                  )}
                </div>
              )}

              {/* Title */}
              <div className="flex items-start gap-2">
                {!readOnly && (
                  <button className="shrink-0 mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Circle className={`w-4 h-4 ${priority.textColor} opacity-40`} />
                    )}
                  </button>
                )}
                <h3 className={`text-sm font-medium ${isCompleted ? 'line-through text-muted-foreground/70' : 'text-foreground'}`}>
                  {task.title}
                </h3>
              </div>

              {/* Description preview */}
              {show('description') && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {description || <span className="text-muted-foreground/40">-</span>}
                </p>
              )}

              {/* Footer: dates, duration, assignee, labels, attachments */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground/70 pt-1 flex-wrap">
                {show('start_date') && (
                  <span className="flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" />
                    {task.start_date ? formatDate(new Date(task.start_date)) : '-'}
                  </span>
                )}
                {show('due_date') && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {task.due_date ? formatDate(new Date(task.due_date)) : '-'}
                  </span>
                )}
                {show('duration') && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {task.duration
                      ? (task.duration >= 60
                        ? t('ui_task.duration.hours', { h: Math.floor(task.duration / 60) })
                        : t('ui_task.duration.minutes', { m: task.duration }))
                      : '-'}
                  </span>
                )}
                {show('assignee') && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {task.assigned_to?.length || 0}
                  </span>
                )}
                {show('labels') && (
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {task.label_ids?.length || 0}
                  </span>
                )}
                {show('attachments') && (
                  <span className="flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />
                    {task.attachments?.length || 0}
                  </span>
                )}
                <div className="hidden md:contents">
                  {customFieldDefs.map(def => {
                    if (!fields.has(`cf_${def.id}`)) return null;
                    const val = gvRenderCustom(def, task.custom_fields?.[def.id], language);
                    if (val == null || val === '') return null;
                    return (
                      <span key={def.id} className="flex items-center gap-1">
                        <span className="text-muted-foreground/70">{def.name}:</span>
                        <span className="truncate max-w-[120px]">{val}</span>
                      </span>
                    );
                  })}
                </div>
                {task.subtask_count > 0 && (
                  <span className="ml-auto">
                    ✓ {task.completed_subtask_count || 0}/{task.subtask_count}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {tasks.length === 0 && (
        <div className="col-span-full text-center py-12 text-muted-foreground">
          <p className="text-sm">{t('tasks.no_tasks')}</p>
          <p className="text-xs mt-1">{t('tasks.gallery_empty_hint')}</p>
        </div>
      )}
    </div>
  );
};

export default GalleryView;
